import {useEffect, useMemo, useState} from "react";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "@/components/ui/command";
import {Button} from "@/components/ui/button.tsx";
import {useLazySearchChipsQuery} from "@/store/chipAssignmentApi.ts";
import {ChipSearchResult} from "@/types/chipAssignment";

interface ChipSearchBarProps {
    onSelect: (chip: ChipSearchResult) => void;
    disabled?: boolean;
    placeholder?: string;
}

// חיפוש Chip לפי קוד פנימי/חיצוני/ת.ז./טלפון בעלים.
export function ChipSearchBar({
    onSelect,
    disabled,
    placeholder = "חיפוש לפי קוד פנימי / קוד חיצוני / ת.ז. / טלפון",
}: ChipSearchBarProps) {
    const [open, setOpen] = useState(false);
    const [term, setTerm] = useState("");
    const [trigger, {data, isFetching}] = useLazySearchChipsQuery();

    useEffect(() => {
        const q = term.trim();
        if (!q) return;
        const handle = setTimeout(() => trigger(q), 300);
        return () => clearTimeout(handle);
    }, [term, trigger]);

    const results = useMemo(() => data ?? [], [data]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    type="button"
                    className="w-full justify-start text-base h-11 font-normal"
                    disabled={disabled}
                >
                    {placeholder}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[420px] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput
                        value={term}
                        onValueChange={setTerm}
                        placeholder={placeholder}
                        className="text-base"
                    />
                    <CommandList>
                        {isFetching && <CommandEmpty>מחפש...</CommandEmpty>}
                        {!isFetching && term.trim() && results.length === 0 && (
                            <CommandEmpty>לא נמצאו תוצאות</CommandEmpty>
                        )}
                        <CommandGroup>
                            {results.map((chip) => (
                                <CommandItem
                                    key={chip.id}
                                    value={String(chip.id)}
                                    onSelect={() => {
                                        onSelect(chip);
                                        setOpen(false);
                                    }}
                                    className="text-base flex flex-col items-start gap-0.5"
                                >
                                    <span className="font-medium">
                                        {chip.internal_code}
                                        {chip.learning_user && chip.learning_user_name
                                            ? ` — משויך ל: ${chip.learning_user_name}`
                                            : ''}
                                    </span>
                                    <span className="text-sm text-muted-foreground">
                                        {[chip.external_number, chip.tz, (chip.phones || []).join(', ')]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
